/**
 * My commissions (BE-6a/b) — the logged-in agent's own statement.
 *
 * Route: /my-commissions
 * The home page for an agent with no operational permissions; a menu entry for every other commission recipient.
 * Reads /api/commissions/mine*, which need no permission.
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, Tabs, Tab, Alert, TablePagination, CircularProgress, IconButton, Tooltip, Card, CardContent,
  Stack, useTheme, useMediaQuery,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { SummaryCardGrid, DataTable, StatusChip, CurrencyText } from '../../components/accounting';
import { fontSize as fs, fontWeight as fw } from '../../utils/accounting/styles';
import { formatCurrency, formatDate } from '../../utils/accounting/formatters';
import { commissionService } from '../../services/accounting/commission.service';
import { serverMessage } from '../../utils/apiConvert';
import { commissionStatus, rateText } from './Commissions';

const DIRECTION = {
  PAID: { label: 'Paid to you', tone: 'success' },
  RECOVERED: { label: 'You returned', tone: 'info' },
  WRITTEN_OFF: { label: 'Written off', tone: 'warning' },
};

const COMMISSION_COLUMNS = [
  { key: 'createdAt', label: 'Date', width: '11%', render: (c) => <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{formatDate(c.createdAt)}</Box> },
  { key: 'task', label: 'Task', width: '20%', render: (c) => (
    <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold }}>{c.taskName || c.taskNumber || '—'}</Typography>
  ) },
  { key: 'invoiceNumber', label: 'Invoice', width: '13%' },
  { key: 'rate', label: 'Rate', width: '9%', align: 'right', render: rateText },
  { key: 'basisAmount', label: 'Basis', width: '13%', align: 'right', render: (c) => <CurrencyText amount={c.basisAmount} /> },
  { key: 'amount', label: 'Commission', width: '13%', align: 'right', render: (c) => <CurrencyText amount={c.amount} bold /> },
  { key: 'status', label: 'Status', width: '21%', render: (c) => {
    const s = commissionStatus(c);
    return (
      <Box display="flex" gap={0.5} flexWrap="wrap">
        <StatusChip tone={s.tone} label={s.label} />
        {c.owedBack && <StatusChip tone="error" label="Owes back" />}
      </Box>
    );
  } },
];

const PAYMENT_COLUMNS = [
  { key: 'paymentNumber', label: 'Payment #', width: '16%', render: (p) => (
    <Typography sx={{ fontSize: fs.xs, fontWeight: fw.semibold, color: 'primary.main' }}>{p.paymentNumber}</Typography>
  ) },
  { key: 'paidAt', label: 'Date', width: '12%', render: (p) => <Box component="span" sx={{ whiteSpace: 'nowrap' }}>{formatDate(p.paidAt)}</Box> },
  { key: 'direction', label: 'Type', width: '16%', render: (p) => <StatusChip tone={DIRECTION[p.direction]?.tone} label={DIRECTION[p.direction]?.label || p.direction} /> },
  { key: 'amount', label: 'Amount', width: '14%', align: 'right', render: (p) => <CurrencyText amount={p.amount} bold /> },
  { key: 'reference', label: 'Reference', width: '28%', render: (p) => p.reference || '—' },
  { key: 'status', label: 'Status', width: '14%', render: (p) => (
    <StatusChip tone={p.status === 'REVERSED' ? 'default' : 'success'} label={p.status === 'REVERSED' ? 'Reversed' : 'Recorded'} />
  ) },
];

const MyCommissions = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [tab, setTab] = useState(0);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(0);
  const [data, setData] = useState({ content: [], totalElements: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, d] = await Promise.all([
        commissionService.getMySummary(),
        tab === 0 ? commissionService.getMine(page, 20) : commissionService.getMyPayments(page, 20),
      ]);
      setSummary(s);
      setData(d);
    } catch (err) {
      setError(serverMessage(err, 'Failed to load your commissions'));
    } finally {
      setLoading(false);
    }
  }, [tab, page]);

  useEffect(() => { load(); }, [load]);

  const owed = Number(summary?.receivable || 0);
  const aging = summary?.aging || {};
  const spinner = <Box display="flex" justifyContent="center" py={4}><CircularProgress /></Box>;
  const empty = (text) => (
    <Paper variant="outlined" sx={{ py: 4, textAlign: 'center' }}>
      <Typography color="text.secondary" sx={{ fontSize: fs.sm }}>{text}</Typography>
    </Paper>
  );

  const summaryCards = [
    { label: 'Earned', value: summary?.earned, color: 'primary' },
    { label: 'Paid to You', value: summary?.paid, color: 'success' },
    { label: 'Ready to Pay', value: summary?.payable, color: 'warning' },
    { label: 'Not Yet Paid', value: summary?.outstanding, color: 'info' },
  ];

  const commissions = isMobile ? (
    <Stack spacing={1.5}>
      {loading ? spinner : data.content.length === 0 ? empty('No commissions yet') : data.content.map((c) => {
        const s = commissionStatus(c);
        return (
          <Card key={c.id} variant="outlined">
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
                <Box>
                  <Typography fontWeight={600} color="primary" sx={{ fontSize: '0.95rem' }}>{c.taskName || c.taskNumber || 'Task'}</Typography>
                  <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>{c.invoiceNumber} · {formatDate(c.createdAt)}</Typography>
                </Box>
                <StatusChip tone={s.tone} label={s.label} />
              </Box>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="caption" color="text.secondary">{rateText(c)} of {formatCurrency(c.basisAmount)}</Typography>
                <Typography fontWeight={700} color="primary" sx={{ fontSize: '1rem' }}>{formatCurrency(c.amount)}</Typography>
              </Box>
            </CardContent>
          </Card>
        );
      })}
    </Stack>
  ) : (
    <DataTable columns={COMMISSION_COLUMNS} data={data.content} loading={loading} pagination={false} emptyMessage="No commissions yet" />
  );

  const payments = isMobile ? (
    <Stack spacing={1.5}>
      {loading ? spinner : data.content.length === 0 ? empty('No payments yet') : data.content.map((p) => (
        <Card key={p.id} variant="outlined">
          <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
            <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1}>
              <Box>
                <Typography fontWeight={600} color="primary" sx={{ fontSize: '0.95rem' }}>{p.paymentNumber}</Typography>
                <Typography color="text.secondary" sx={{ fontSize: '0.8rem' }}>{formatDate(p.paidAt)}{p.reference ? ` · ${p.reference}` : ''}</Typography>
              </Box>
              <StatusChip tone={p.status === 'REVERSED' ? 'default' : DIRECTION[p.direction]?.tone} label={p.status === 'REVERSED' ? 'Reversed' : DIRECTION[p.direction]?.label} />
            </Box>
            <Typography fontWeight={700} color="primary" textAlign="right" sx={{ fontSize: '1rem' }}>{formatCurrency(p.amount)}</Typography>
          </CardContent>
        </Card>
      ))}
    </Stack>
  ) : (
    <DataTable columns={PAYMENT_COLUMNS} data={data.content} loading={loading} pagination={false} emptyMessage="No payments yet" />
  );

  const tabSx = { textTransform: 'none', fontSize: fs.sm };

  return (
    <Box sx={{ width: 0, minWidth: '100%' }}>
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="h5" fontWeight={600} color="secondary.main">My Commissions</Typography>
        <Tooltip title="Refresh"><IconButton size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton></Tooltip>
      </Box>

      {owed > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          You owe back {formatCurrency(owed)} — commission you were paid on invoices that were later cancelled.
        </Alert>
      )}

      <SummaryCardGrid cards={summaryCards} loading={!summary && loading} sx={{ mb: Number(summary?.outstanding) > 0 ? 1 : 2 }} />
      {Number(summary?.outstanding) > 0 && (
        <Typography sx={{ fontSize: fs.xs, color: 'text.secondary', mb: 2 }}>
          Not yet paid, by age: under 30 days {formatCurrency(aging.under30)} · 30–60 days {formatCurrency(aging.from30To60)} · over 60 days {formatCurrency(aging.over60)}
        </Typography>
      )}

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2, width: 0, minWidth: '100%' }}>
        <Tabs value={tab} onChange={(_, v) => { setTab(v); setPage(0); setData({ content: [], totalElements: 0 }); }} variant="scrollable" scrollButtons="auto">
          <Tab label="My Commissions" sx={tabSx} />
          <Tab label="My Payments" sx={tabSx} />
        </Tabs>
      </Box>

      {tab === 0 ? commissions : payments}
      {data.totalElements > 0 && (
        <TablePagination component="div" count={data.totalElements} page={page} rowsPerPage={20}
          onPageChange={(_, p) => setPage(p)} rowsPerPageOptions={[20]}
          sx={{ '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: fs.xs } }} />
      )}
    </Box>
  );
};

export default MyCommissions;
